import React, { useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { IntakeForm } from "@/components/public/IntakeForm";
import { Phone, Mail, MessageCircle } from "lucide-react";

const CONTAINER = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

export default function Contact() {
  const { content } = useOutletContext();
  const [params] = useSearchParams();
  const isChat = params.get("source") === "live-chat";
  const phone = content?.phone;
  const footer = content?.footer || {};

  useEffect(() => {
    document.title = (isChat ? "Start a Chat" : "Contact Us") + " · MovingMyMama";
  }, [isChat]);

  return (
    <div data-testid="contact-page" className="py-14 sm:py-20">
      <div className={CONTAINER}>
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-5">
            <p className="text-[14px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">
              {isChat ? "Start a live chat" : "We're here to help"}
            </p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-display font-bold tracking-tight text-[#1C4358] leading-tight">
              {isChat ? "Chat with a caring guide" : "Reach out — we'd love to help."}
            </h1>
            <p className="mt-5 text-lg text-[#4B5563] leading-relaxed">
              {isChat
                ? "Leave your details and a guide will connect with you right away. No pressure, no cost — just real help when you need it."
                : "Have a question or just want to talk it through? Send us a message and a caring guide will get back to you — usually the same day."}
            </p>

            <div className="mt-8 space-y-4">
              {phone && (
                <a href={telHref(phone)} data-testid="contact-phone" className="flex items-center gap-4 rounded-[0.9rem] bg-[#FBF6EC] border border-[#1C4358]/10 px-5 py-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#1C4358] text-white"><Phone size={20} /></span>
                  <span><span className="block text-[13px] text-[#4B5563]">Call us</span><span className="text-[17px] font-semibold text-[#1C4358]">{phone}</span></span>
                </a>
              )}
              {footer.email && (
                <a href={`mailto:${footer.email}`} data-testid="contact-email" className="flex items-center gap-4 rounded-[0.9rem] bg-[#FBF6EC] border border-[#1C4358]/10 px-5 py-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#1C4358] text-white"><Mail size={20} /></span>
                  <span><span className="block text-[13px] text-[#4B5563]">Email us</span><span className="text-[17px] font-semibold text-[#1C4358]">{footer.email}</span></span>
                </a>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-[1.2rem] bg-white border border-[#1C4358]/10 shadow-md shadow-[#1C4358]/10 p-6 sm:p-9">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#9C1E22]/10 text-[#9C1E22]">
                  <MessageCircle size={24} />
                </span>
                <div>
                  <h2 className="text-xl font-display font-semibold text-[#1C4358]">
                    {isChat ? "Start your chat request" : "Send us a message"}
                  </h2>
                  <p className="text-[14px] text-[#4B5563]">We'll reach out personally.</p>
                </div>
              </div>
              <IntakeForm
                cta={isChat ? "Start Live Chat" : "Contact Form"}
                source={isChat ? "CTA: Start Live Chat" : "Contact Form"}
                submitLabel={isChat ? "Request a live chat" : "Send my message"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
